import React, { useState, useEffect } from 'react';
import { Account, Loan, CreateLoanRequest, UpdateLoanRequest, LoanType, LoanStatus } from '../../apiServices/types';
import { loanService } from '../../apiServices/loanService';
import { formatCurrency } from '../../utils';
import { Modal, ConfirmationModal, GlassSelect } from '../UI';
import './LoanModal.css';

// Helper function to convert string loanType to LoanType enum
const parseLoanType = (loanType: LoanType | string): LoanType => {
  if (typeof loanType === 'string') {
    if (loanType === 'ILent') return LoanType.ILent;
    if (loanType === 'IBorrowed') return LoanType.IBorrowed;
  }
  return loanType as LoanType;
};

// Helper function to convert string status to LoanStatus enum
const parseLoanStatus = (status: LoanStatus | string): LoanStatus => {
  if (typeof status === 'string') {
    if (status === 'Active') return LoanStatus.Active;
    if (status === 'PartiallyRepaid') return LoanStatus.PartiallyRepaid;
    if (status === 'Repaid') return LoanStatus.Repaid;
  }
  return status as LoanStatus;
};

interface LoanModalProps {
  isOpen: boolean;
  accounts: Account[];
  loan?: Loan | null;
  onClose: () => void;
  onSuccess: (updatedLoan?: Loan) => void;
}

const LoanModal: React.FC<LoanModalProps> = ({
  isOpen,
  accounts,
  loan,
  onClose,
  onSuccess
}) => {
  const [contactName, setContactName] = useState('');
  const [amount, setAmount] = useState('');
  const [repaidAmount, setRepaidAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [loanType, setLoanType] = useState<LoanType>(LoanType.ILent);
  const [status, setStatus] = useState<LoanStatus>(LoanStatus.Active);
  const [date, setDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const isEditMode = !!loan;

  const getDateOnly = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (isOpen) {
      if (loan) {
        setContactName(loan.contactName);
        setAmount(loan.amount.toString());
        setRepaidAmount(loan.repaidAmount.toString());
        setAccountId(loan.accountId || '');
        setLoanType(parseLoanType(loan.loanType));
        setStatus(parseLoanStatus(loan.status));
        setDate(loan.date ? getDateOnly(loan.date) : getTodayDate());
        setDueDate(loan.dueDate ? getDateOnly(loan.dueDate) : '');
        setDescription(loan.description || '');
      } else {
        setContactName('');
        setAmount('');
        setRepaidAmount('0');
        setAccountId('');
        setLoanType(LoanType.ILent);
        setStatus(LoanStatus.Active);
        setDate(getTodayDate());
        setDueDate('');
        setDescription('');
      }
      setErrors({});
      setShowConfirmClose(false);
    }
  }, [isOpen, loan]);

  const hasFormChanges = () => {
    if (!isEditMode) {
      return contactName !== '' || amount !== '' || accountId !== '' || description !== '' || date !== getTodayDate() || dueDate !== '';
    }
    
    if (!loan) return false;
    
    const loanDateOnly = loan.date ? getDateOnly(loan.date) : getTodayDate();
    const loanDueDateOnly = loan.dueDate ? getDateOnly(loan.dueDate) : '';
    
    return (
      contactName !== loan.contactName ||
      amount !== loan.amount.toString() ||
      repaidAmount !== loan.repaidAmount.toString() ||
      accountId !== (loan.accountId || '') ||
      loanType !== parseLoanType(loan.loanType) ||
      status !== parseLoanStatus(loan.status) ||
      date !== loanDateOnly ||
      dueDate !== loanDueDateOnly ||
      description !== (loan.description || '')
    );
  };

  const handleCloseAttempt = () => {
    if (hasFormChanges()) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!contactName.trim()) {
      newErrors.contactName = 'Ім\'я контакту обов\'язкове';
    }

    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = 'Введіть суму більшу за нуль';
    }

    if (repaidAmount && parseFloat(repaidAmount) < 0) {
      newErrors.repaidAmount = 'Повернена сума не може бути від\'ємною';
    }

    if (repaidAmount && parseFloat(repaidAmount) > parseFloat(amount || '0')) {
      newErrors.repaidAmount = 'Повернена сума не може перевищувати суму позики';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      if (isEditMode && loan) {
        const request: UpdateLoanRequest = {
          contactName: contactName.trim(),
          amount: parseFloat(amount),
          repaidAmount: parseFloat(repaidAmount || '0'),
          accountId: accountId || undefined,
          loanType: loanType,
          status: status,
          date: date || undefined,
          dueDate: dueDate || undefined,
          description: description.trim() || undefined
        };

        const updatedLoan = await loanService.update(loan.id, request);
        onSuccess(updatedLoan);
      } else {
        const request: CreateLoanRequest = {
          contactName: contactName.trim(),
          amount: parseFloat(amount),
          accountId: accountId || undefined,
          loanType: loanType,
          date: date || undefined,
          dueDate: dueDate || undefined,
          description: description.trim() || undefined
        };

        await loanService.create(request);
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      setErrors({ general: error.message || (isEditMode ? 'Помилка оновлення позики' : 'Помилка створення позики') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        title={isEditMode ? 'Редагувати позику' : 'Створити позику'}
        onClose={handleCloseAttempt}
        size="md"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseAttempt}
              disabled={isSubmitting}
            >
              Скасувати
            </button>
            <button
              form="loan-form"
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (isEditMode ? 'Оновлення...' : 'Створення...') : (isEditMode ? 'Оновити позику' : 'Створити позику')}
            </button>
          </>
        }
      >
        <form id="loan-form" onSubmit={handleSubmit} className="loan-form">
          <div className="form-group">
            <label htmlFor="loan-type">Тип позики</label>
            <GlassSelect
              value={String(loanType)}
              onChange={(value) => setLoanType(Number(value) as LoanType)}
              options={[
                { value: String(LoanType.ILent), label: 'Я позичив комусь' },
                { value: String(LoanType.IBorrowed), label: 'Мені позичили' }
              ]}
              placeholder="Оберіть тип позики"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact-name">Ім'я контакту</label>
            <input
              type="text"
              id="contact-name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className={errors.contactName ? 'form-input error' : 'form-input'}
              placeholder="Введіть ім'я контакту"
              required
            />
            {errors.contactName && (
              <span className="error-message">{errors.contactName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="amount">Сума позики</label>
            <input
              type="number"
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={errors.amount ? 'form-input error' : 'form-input'}
              placeholder="0.00"
              required
              min="0.01"
              step="0.01"
            />
            {errors.amount && (
              <span className="error-message">{errors.amount}</span>
            )}
          </div>

          {isEditMode && (
            <div className="form-group">
              <label htmlFor="repaid-amount">Повернена сума</label>
              <input
                type="number"
                id="repaid-amount"
                value={repaidAmount}
                onChange={(e) => setRepaidAmount(e.target.value)}
                className={errors.repaidAmount ? 'form-input error' : 'form-input'}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
              {errors.repaidAmount && (
                <span className="error-message">{errors.repaidAmount}</span>
              )}
            </div>
          )}

          {isEditMode && (
            <div className="form-group">
              <label htmlFor="status">Статус</label>
              <GlassSelect
                value={String(status)}
                onChange={(value) => setStatus(Number(value) as LoanStatus)}
                options={[
                  { value: String(LoanStatus.Active), label: 'Активна' },
                  { value: String(LoanStatus.PartiallyRepaid), label: 'Частково повернено' },
                  { value: String(LoanStatus.Repaid), label: 'Повернено' }
                ]}
                placeholder="Оберіть статус"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="account">Рахунок (опціонально)</label>
            <GlassSelect
              value={accountId}
              onChange={(value) => setAccountId(value)}
              options={[
                { value: '', label: 'Без рахунку' },
                ...accounts.map(account => ({
                  value: account.id,
                  label: `${account.name} (баланс: ${formatCurrency(account.currentBalance)})`
                }))
              ]}
              placeholder="Оберіть рахунок"
            />
          </div>

          <div className="form-group">
            <label htmlFor="loan-date">Дата</label>
            <div className="date-input-wrapper">
              <input
                type="date"
                id="loan-date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="form-input date-input-custom"
                required
              />
              <label htmlFor="loan-date" className="date-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="due-date">Термін повернення (опціонально)</label>
            <div className="date-input-wrapper">
              <input
                type="date"
                id="due-date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="form-input date-input-custom"
              />
              <label htmlFor="due-date" className="date-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Опис</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-input"
              placeholder="Додайте опис позики"
              rows={3}
            />
          </div>

          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={showConfirmClose}
        title={isEditMode ? 'Скасувати редагування позики?' : 'Скасувати створення позики?'}
        message="У вас є незбережені зміни. Ви впевнені, що хочете закрити форму без збереження?"
        confirmText="Так"
        cancelText={isEditMode ? 'Ні' : 'Ні'}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        variant="warning"
      />
    </>
  );
};

export default LoanModal;

